import React from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
} from "react-native";
import Text from "app/components/Text";
import MomentBullet from "app/components/chapter/MomentBullet";
import apiActions from "app/utils/apiActions";
import valueActions from "app/utils/valueActions";
import chapterActions from "app/utils/chapterActions";
import { StyleContext } from "app/contexts/Style";

// Loose Ends — a closure ritual for items pinned in the past and never
// completed. One card per lingering value; each gets a disposition:
//   ✓ complete (backdated) · view · ↻ pin (to today) · ✗ archive ·
//   skip (push to end of queue)
const SORTS = [
  { key: "oldest", label: "oldest" },
  { key: "invested", label: "most carried" },
  { key: "newest", label: "newest" },
  { key: "height", label: "tallest" }, // deepest child hierarchies first
];

// how many cards are on screen at once: 1 = card-by-card ritual,
// larger = a working list (display-only, no refetch)
const LIMITS = [
  { key: 1, label: "1" },
  { key: 10, label: "10" },
  { key: 25, label: "25" },
  { key: 50, label: "50" },
];

const fmtDate = (t) => {
  if (!t) return "?";
  const d = new Date(t);
  return `${d.toLocaleString("default", { month: "short" })} ${d.getDate()} ’${String(
    d.getFullYear()
  ).slice(2)}`;
};

const isoDay = (t) => {
  const d = t ? new Date(t) : new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export default class LooseEnds extends React.Component {
  static contextType = StyleContext;

  state = {
    loading: true,
    error: null,
    items: [],
    idx: 0,
    sort: "oldest",
    limit: 1,
    orphans: false,
    mode: null, // null | 'done' | 'archive'
    comment: "",
    doneDate: "",
    busy: false,
    tally: { done: 0, archived: 0, pinned: 0 },
  };

  componentDidMount() {
    this.load();
  }

  load = async ({ sort = this.state.sort, orphans = this.state.orphans } = {}) => {
    this.setState({ loading: true, error: null, mode: null, comment: "" });
    const data = { sort };
    if (orphans) data.orphans = true;
    const res = await apiActions.request("values/loose_ends", "GET", data);
    if (res?.success) {
      this.setState({ loading: false, items: res.items || [], idx: 0, sort, orphans });
    } else {
      this.setState({ loading: false, error: "Couldn't load the queue." });
    }
  };

  current = () => this.state.items[this.state.idx];

  advance = (tallyKey) => {
    this.setState((s) => ({
      idx: s.idx + 1,
      mode: null,
      comment: "",
      doneDate: "",
      busy: false,
      tally: tallyKey ? { ...s.tally, [tallyKey]: s.tally[tallyKey] + 1 } : s.tally,
    }));
  };

  skip = () => {
    this.setState((s) => {
      const items = s.items.slice();
      const [cur] = items.splice(s.idx, 1);
      items.push(cur);
      return { items, mode: null, comment: "" };
    });
  };

  postComment = async (v, text) => {
    if (!text?.trim()) return;
    await apiActions.request("moments", "POST", {
      moment: { chapter_id: "today", value_id: v.id, text: text.trim() },
    });
  };

  confirmDone = async () => {
    const v = this.current();
    this.setState({ busy: true });
    const date = this.state.doneDate || isoDay(v.last_pinned_at);
    await valueActions.update(v.id, { completed_at: date });
    await this.postComment(v, this.state.comment);
    this.advance("done");
  };

  confirmArchive = async () => {
    const v = this.current();
    this.setState({ busy: true });
    await valueActions.update(v.id, { archived_at: new Date().toISOString() });
    await this.postComment(v, this.state.comment);
    this.advance("archived");
  };

  pinToday = async () => {
    const v = this.current();
    this.setState({ busy: true });
    await chapterActions.pinValue("today", v.id);
    this.advance("pinned");
  };

  /* list mode: actions apply immediately and the row leaves the list */

  removeItem = (id, tallyKey) => {
    this.setState((s) => ({
      items: s.items.filter((i) => i.id !== id),
      tally: tallyKey ? { ...s.tally, [tallyKey]: s.tally[tallyKey] + 1 } : s.tally,
    }));
  };

  completeItem = (v) => {
    valueActions.update(v.id, { completed_at: isoDay(v.last_pinned_at) });
    this.removeItem(v.id, "done");
  };

  archiveItem = (v) => {
    valueActions.update(v.id, { archived_at: new Date().toISOString() });
    this.removeItem(v.id, "archived");
  };

  pinItem = (v) => {
    chapterActions.pinValue("today", v.id);
    this.removeItem(v.id, "pinned");
  };

  skipItem = (v) => {
    this.setState((s) => {
      const items = s.items.filter((i) => i.id !== v.id);
      items.push(v);
      return { items };
    });
  };

  /* ── render helpers ── */

  renderMeta = (v) => {
    const c = this.context.colors;
    return (
      <Text style={{ fontSize: 12.5, color: c.inactive, marginBottom: 12 }}>
        pinned {v.pin_count}×{"  ·  "}
        {fmtDate(v.first_pinned_at)} – {fmtDate(v.last_pinned_at)}
        {"  ·  "}
        {v.engaged ? "you engaged with it" : "never engaged"}
        {v.height > 0 ? `  ·  ${v.height} generation${v.height > 1 ? "s" : ""} below` : ""}
      </Text>
    );
  };

  Chip = ({ on, label, onPress }) => {
    const c = this.context.colors;
    return (
      <TouchableOpacity
        onPress={onPress}
        style={{
          paddingHorizontal: 11,
          paddingVertical: 5,
          borderRadius: 9,
          marginRight: 6,
          marginBottom: 6,
          backgroundColor: on ? c.active : c.fill,
        }}
      >
        <Text style={{ fontSize: 12, color: on ? c.empty : c.foreground }}>{label}</Text>
      </TouchableOpacity>
    );
  };

  Btn = ({ label, onPress, accent, disabled }) => {
    const c = this.context.colors;
    return (
      <TouchableOpacity
        onPress={onPress}
        disabled={disabled}
        style={{
          borderWidth: 1,
          borderColor: accent ? c.active : c.trim,
          borderRadius: 10,
          paddingHorizontal: 14,
          paddingVertical: 9,
          marginRight: 8,
          marginBottom: 8,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Text style={{ fontSize: 13.5, color: accent ? c.active : c.foreground }}>{label}</Text>
      </TouchableOpacity>
    );
  };

  renderFollowUp() {
    const c = this.context.colors;
    const v = this.current();
    const { mode, comment, doneDate, busy } = this.state;
    const input = (placeholder) => (
      <TextInput
        value={comment}
        onChangeText={(t) => this.setState({ comment: t })}
        placeholder={placeholder}
        placeholderTextColor={c.placeholder}
        multiline
        style={{
          borderWidth: 1,
          borderColor: c.trim,
          borderRadius: 10,
          padding: 10,
          minHeight: 54,
          marginBottom: 10,
          color: c.foreground,
          fontSize: 14,
          textAlignVertical: "top",
        }}
      />
    );

    if (mode === "done") {
      return (
        <View>
          <Text style={{ fontSize: 12, color: c.inactive, marginBottom: 4 }}>
            completed on (defaults to last activity):
          </Text>
          <TextInput
            value={doneDate || isoDay(v.last_pinned_at)}
            onChangeText={(t) => this.setState({ doneDate: t })}
            placeholder="YYYY-MM-DD"
            placeholderTextColor={c.placeholder}
            style={{
              borderWidth: 1,
              borderColor: c.trim,
              borderRadius: 10,
              padding: 10,
              marginBottom: 10,
              color: c.foreground,
              fontSize: 14,
              width: 140,
            }}
          />
          {input("how did it go? (optional)")}
          <View style={{ flexDirection: "row" }}>
            <this.Btn label="✓ complete" accent onPress={this.confirmDone} disabled={busy} />
            <this.Btn label="back" onPress={() => this.setState({ mode: null })} />
          </View>
        </View>
      );
    }
    if (mode === "archive") {
      return (
        <View>
          {input("closing words? (optional)")}
          <View style={{ flexDirection: "row" }}>
            <this.Btn label="✗ archive" accent onPress={this.confirmArchive} disabled={busy} />
            <this.Btn label="back" onPress={() => this.setState({ mode: null })} />
          </View>
        </View>
      );
    }
    return (
      <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
        <this.Btn label="✓ complete" onPress={() => this.setState({ mode: "done" })} />
        <this.Btn
          label="view"
          onPress={() => this.props.navigation.navigate("Value", { id: v.id })}
        />
        <this.Btn label="↻ pin" onPress={this.pinToday} disabled={busy} />
        <this.Btn label="✗ archive" onPress={() => this.setState({ mode: "archive" })} />
        <this.Btn label="skip" onPress={this.skip} />
      </View>
    );
  }

  render() {
    const c = this.context.colors;
    const { loading, error, items, idx, sort, limit, orphans, tally } = this.state;
    const v = this.current();
    const finished =
      !loading && !error && (limit === 1 ? idx >= items.length : items.length === 0);

    return (
      <ScrollView
        style={{ flex: 1, backgroundColor: c.empty }}
        contentContainerStyle={{ padding: 20, maxWidth: 560, width: "100%", alignSelf: "center" }}
      >
        <View style={{ flexDirection: "row", alignItems: "baseline", marginBottom: 4 }}>
          <Text style={{ fontSize: 22, fontWeight: "700", color: c.foreground, flex: 1 }}>
            Loose Ends
          </Text>
          {!loading && !finished ? (
            <Text style={{ fontSize: 13, color: c.inactive }}>
              {limit === 1
                ? `${idx + 1} of ${items.length}`
                : `showing ${Math.min(limit, items.length)} of ${items.length}`}
            </Text>
          ) : null}
        </View>
        <Text style={{ fontSize: 12.5, color: c.inactive, marginBottom: 12 }}>
          things you pinned, then life moved on — give each one an ending
        </Text>

        <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 6 }}>
          {SORTS.map((s) => (
            <this.Chip
              key={s.key}
              on={sort === s.key}
              label={s.label}
              onPress={() => this.load({ sort: s.key })}
            />
          ))}
        </View>
        <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
          <Text style={{ fontSize: 12, color: c.inactive, marginRight: 6 }}>show</Text>
          {LIMITS.map((l) => (
            <this.Chip
              key={l.key}
              on={limit === l.key}
              label={l.label}
              onPress={() => this.setState({ limit: l.key, idx: 0, mode: null, comment: "" })}
            />
          ))}
          <this.Chip
            on={orphans}
            label="orphans only"
            onPress={() => this.load({ orphans: !orphans })}
          />
        </View>

        {loading ? (
          <ActivityIndicator color={c.active} style={{ marginTop: 30 }} />
        ) : error ? (
          <TouchableOpacity onPress={() => this.load()}>
            <Text style={{ color: c.active, textDecorationLine: "underline" }}>
              {error} tap to retry
            </Text>
          </TouchableOpacity>
        ) : finished ? (
          <View style={{ marginTop: 20 }}>
            <Text style={{ fontSize: 16, color: c.foreground, marginBottom: 8 }}>
              {items.length
                ? "All ends tied. 🕯"
                : "Nothing lingering — the past is at peace."}
            </Text>
            {items.length ? (
              <Text style={{ fontSize: 13.5, color: c.inactive, lineHeight: 21 }}>
                completed {tally.done} · archived {tally.archived} · pinned {tally.pinned}
              </Text>
            ) : null}
            <TouchableOpacity onPress={() => this.load()} style={{ marginTop: 14 }}>
              <Text style={{ color: c.active, textDecorationLine: "underline", fontSize: 13 }}>
                check again
              </Text>
            </TouchableOpacity>
          </View>
        ) : limit === 1 && v ? (
          <View
            style={{
              backgroundColor: c.fill,
              borderRadius: 16,
              borderWidth: 1,
              borderColor: c.trim,
              padding: 18,
            }}
          >
            {/* bullet gives the value badge + expand up/down to parents/children */}
            <View style={{ marginBottom: 6 }}>
              <MomentBullet
                key={`loose-${v.id}`}
                moment={{ value: v, id: `loose-${v.id}` }}
                chapter={{ id: "value-loose-ends" }}
                rootChapter={{ id: "value-loose-ends", is_value: true }}
                parentIsValue
              />
            </View>
            {this.renderMeta(v)}
            {this.renderFollowUp()}
          </View>
        ) : limit > 1 ? (
          items.slice(0, limit).map((item) => (
            <View
              key={item.id}
              style={{
                backgroundColor: c.fill,
                borderRadius: 16,
                borderWidth: 1,
                borderColor: c.trim,
                padding: 14,
                marginBottom: 10,
              }}
            >
              <View style={{ marginBottom: 4 }}>
                <MomentBullet
                  key={`loose-${item.id}`}
                  moment={{ value: item, id: `loose-${item.id}` }}
                  chapter={{ id: "value-loose-ends" }}
                  rootChapter={{ id: "value-loose-ends", is_value: true }}
                  parentIsValue
                />
              </View>
              {this.renderMeta(item)}
              <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
                <this.Btn label="✓ complete" onPress={() => this.completeItem(item)} />
                <this.Btn
                  label="view"
                  onPress={() => this.props.navigation.navigate("Value", { id: item.id })}
                />
                <this.Btn label="↻ pin" onPress={() => this.pinItem(item)} />
                <this.Btn label="✗ archive" onPress={() => this.archiveItem(item)} />
                <this.Btn label="skip" onPress={() => this.skipItem(item)} />
              </View>
            </View>
          ))
        ) : null}
      </ScrollView>
    );
  }
}
